import {
  Component, OnInit, OnChanges, OnDestroy, ChangeDetectionStrategy, Input, ChangeDetectorRef, SimpleChanges
} from '@angular/core';
import { cloneDeep, get, findIndex, merge, isEmpty } from 'lodash';
import { Subject } from 'rxjs';
import { takeUntil, tap } from 'rxjs/operators';
import * as immutable from 'immutable';

import {
  IdynamicFormItem
} from '@ispm/dynamic-form/interfaces/IdynamicFormItem';
import { IWidgetItem } from '@ispm/widgets/interfaces/widget-item.interface';
import { AgregationService } from '@ispm-client/modules/aggregator/services';
import { IdataBinding } from '@ispm-client/modules/aggregator/interfaces';
import { ConditionalItem, IConditionalItem } from '@ispm/shared/classes/conditional-item.class';
import {DatePipe} from "@angular/common";
import { LineChartDataItem } from './interfaces';
import {ChartUtils} from "@ispm/dynamic-form/components/chart/ChartUtils";
import {InterpolateService} from "@ispm/shared/services/interpolate.service";

@Component({
  selector: 'gen-chart-item-data',
  templateUrl: './chart-item-data.component.html',
  styleUrls: ['./chart-item-data.component.scss'],
  providers: [DatePipe],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ChartItemDataComponent implements OnInit, OnChanges, OnDestroy, IWidgetItem {

  constructor(private cd: ChangeDetectorRef,
              private agregationService: AgregationService,
              private datePipe: DatePipe,
              private interpolator: InterpolateService) { }

  @Input()
  set widgetConfig(_itemConfig: IdynamicFormItem) {
    const payload = _itemConfig.widgetPayload || { };
    this.bindingConfig = merge(this.bindingConfig, payload.binding);
    this.chartDataBinding = payload.chartDataBinding;
    this.chartDataFilters = payload.chartDataFilters;
    this.chartDataFormKey = payload.chartDataFormKey;
    this.chartDataPath = payload.chartDataPath;
    this.chartType = payload.type || this.chartType;
    this.hideLinePoints = payload.hideLinePoints || this.hideLinePoints;
    this.hideMiniLinePoints = payload.hideMiniLinePoints || this.hideMiniLinePoints;
    this.hideCurrentItemAnnotation = payload.hideCurrentItemAnnotation || this.hideCurrentItemAnnotation;
    this.hideCurrent = payload.hideCurrent || false;
    this.groupBy = payload.groupBy;
    this.fixedPointRadius = payload.fixedPointRadius || 0;
    this.showRowHighlight = payload.showRowHighlight || false;
    this.tooltipDisplayPattern = payload.tooltipDisplayPattern || '';
    this.tooltipPosition = payload.tooltipPosition || 'BOTTOM';
    this.tooltipShow = payload.tooltipShow || false;
    this.onlyExpanded =  payload.onlyExpanded || false;
    this.basedOnOriginalData = payload.basedOnOriginalData || false;
    this.isMiniChartAvailable = payload.showMiniChart || true;
    this.hideChartBasedOnValues = payload.hideChartBasedOnValues || [];
    this.checkMiniChartAvailable();
  }

  public chartType = 'line';
  public hideLinePoints = false;
  public showRowHighlight = false;
  public tooltipText: string = '';
  public tooltipPosition: string;
  public tooltipShow: boolean;
  public hideCurrentItemAnnotation = false;
  public hideCurrent : boolean;
  public isMiniChartAvailable: boolean = true;
  private chartDataBinding: IdataBinding;
  private chartDataFilters: IConditionalItem[];
  private chartDataFormKey: string;
  public hideMiniLinePoints = true;
  private chartDataPath: string;
  private groupBy:string;
  private fixedPointRadius: number;
  private bindingConfig = {
    value:    { path: 'value',        fallback: undefined  },
    unit:     { path: 'unit.display', fallback: '' },
    label:    { path: undefined,      fallback: '' },
    subLabel: { path: undefined,      fallback: undefined},
    prettyLabel: { path: undefined,      fallback: ''},
    minValue: { path: undefined,      fallback: undefined },
    maxValue: { path: undefined,      fallback: undefined },
    date:     { path: undefined,      fallback: ''},
    type:     { path: undefined,      fallback: ''},
  };
  private tooltipDisplayPattern: string;
  public onlyExpanded: boolean = false;
  private basedOnOriginalData: boolean = false;
  private hideChartBasedOnValues: Array<string>;

  set widgetData(widgetData: any) {
    this.data = cloneDeep(widgetData);
    this.cd.markForCheck();
    this.cd.detectChanges();
  }

  @Input()
  public data:any;
  @Input()
  public set originalData(_originalData: any) {
    if(this.basedOnOriginalData) {
      this.chartData = _originalData;
      this.parseData();
    }

  };

  public unit = '';
  public label = '';
  public subLabel = '';
  public prettyLabel = '';
  public indicator = '';
  public value: string | Number = '';
  public minValue = Number.NEGATIVE_INFINITY;
  public maxValue = Number.POSITIVE_INFINITY;
  public isOutOfRange = false;
  public date = '';

  public expandedChart = false;
  public meassurements: LineChartDataItem[] = [];
  public showUnitsWarning = false;
  private chartData: any[] = [];
  private destroy$ = new Subject();

  public ngOnInit() {
    this.loadData();
  }

  public ngOnChanges(changes: SimpleChanges) {
    if (changes.data) {
      this.updateComponentData();
    }
  }

  public ngOnDestroy() {
    this.destroy$.next(true);
    this.destroy$.complete();
  }

  private loadData() {
    if (!this.basedOnOriginalData) {
      this.agregationService.registerQuery([{
        ...this.chartDataBinding,
        identifier: 'resultData'
      }], 'CHART-DATA').pipe(
        takeUntil(this.destroy$),
        tap(result => {
          let data = result[0].resultData;
          if (data instanceof immutable.Iterable) {
            data = (data as immutable.Iterable<any, any>).toArray();
          }
          this.chartData = data;
          this.parseData();
        })
      ).subscribe();
    }

  }


  private parseData() {
    if (!this.chartData || !this.data) { return; }
    if(Array.isArray(this.chartData)) {
      this.meassurements = this.chartData
        .filter(record => { // (optional) pre filter data
          if (!this.chartDataFilters) {
            return true;
          }
          return this.chartDataFilters.every(condition => ConditionalItem.from(record, condition).verify());
        })
        .reduce((list, record) => { // (optional) if data is structured with formKeys, extract data
          if (!this.chartDataFormKey) {
            list.push(record);
            return list;
          }

          if (!isEmpty(this.chartDataPath)) {
            const recordList = get(record, this.chartDataPath)
              .filter(contentItem => contentItem.formKey === this.chartDataFormKey)
              .map(contentItem => contentItem.data);
            return list.concat(recordList);
          } else {
            return list.concat(record);
          }

        }, [])
        .filter(contentData => // (optional) filter remaining data be of the same type
          this.fetchWithBindingConfig(contentData, this.bindingConfig.type) ===
          this.fetchWithBindingConfig(this.data, this.bindingConfig.type)
        )
        .map(contentData => ({ // restructure to common format used by chart
          value: this.fetchWithBindingConfig(contentData, this.bindingConfig.value),
          unit: this.fetchWithBindingConfig(contentData, this.bindingConfig.unit),
          maxValue: this.fetchWithBindingConfig(contentData, this.bindingConfig.maxValue),
          minValue: this.fetchWithBindingConfig(contentData, this.bindingConfig.minValue),
          date: this.fetchWithBindingConfig(contentData, this.bindingConfig.date)
        }))
        .sort((a, b) => {
          const aDate = new Date(a.date)
          const bDate = new Date(b.date)
          if (aDate instanceof Date && bDate instanceof Date) {
            return (aDate.getTime() > bDate.getTime() ? 1 : -1);
          }
          return (a.date > b.date ? 1 : -1);
        });

    }
    const primaryUnit = ChartUtils.getPrimaryUnit(this.meassurements);
    this.showUnitsWarning = !!primaryUnit;
    if (this.date) {
      const itemIndex: number = findIndex(this.meassurements, item => item.date === this.date);
      const previous = itemIndex - 1;
      const previousMeassurement = this.meassurements[previous];
      const compareUnit = primaryUnit ? primaryUnit : this.unit;
      if (this.unit === compareUnit && previousMeassurement && previousMeassurement.unit === compareUnit) {
        const value = parseFloat(`${this.value}`);
        if (value > previousMeassurement.value) {
          this.indicator = '▲';
        } else if (value < previousMeassurement.value) {
          this.indicator = '▼';
        } else {
          this.indicator = '';
        }
      }
    }
    this.cd.markForCheck();
  }

  private updateComponentData() {
    if (!this.data) { return; }
    const data = this.data;
    this.unit     = this.fetchWithBindingConfig(data, this.bindingConfig.unit);
    this.label    = this.fetchWithBindingConfig(data, this.bindingConfig.label);
    this.value    = this.fetchWithBindingConfig(data, this.bindingConfig.value);
    this.minValue = this.fetchWithBindingConfig(data, this.bindingConfig.minValue);
    this.maxValue = this.fetchWithBindingConfig(data, this.bindingConfig.maxValue);
    this.date     = this.fetchWithBindingConfig(data, this.bindingConfig.date);
    this.subLabel = this.fetchWithBindingConfig(data, this.bindingConfig.subLabel);
    this.prettyLabel = this.fetchWithBindingConfig(data, this.bindingConfig.prettyLabel);
    this.tooltipText = this.formatTooltipText();

    const rangeMinValue = this.minValue === undefined ? Number.NEGATIVE_INFINITY : this.minValue;
    const rangeMaxValue = this.maxValue === undefined ? Number.POSITIVE_INFINITY : this.maxValue;
    this.isOutOfRange =
      parseFloat(`${this.value}`) < rangeMinValue ||
      parseFloat(`${this.value}`) > rangeMaxValue;

    this.parseData();
    this.checkMiniChartAvailable();
  }

  private fetchWithBindingConfig(data, bindingConfig) {
    if(bindingConfig.hasOwnProperty('format') && get(bindingConfig, 'format')){
      if(!isNaN(Date.parse(get(data, bindingConfig.path)))){
        const formattedDate = this.datePipe.transform(get(data, bindingConfig.path), get(bindingConfig, 'format'));
        return formattedDate;
      }

    }
    return get(data, bindingConfig.path, bindingConfig.fallback);
  }

  private formatTooltipText() {
    return this.interpolator.interpolate(this.tooltipDisplayPattern, this, true);;
  }

  private checkMiniChartAvailable() {
    this.isMiniChartAvailable = true;
    if(this.hideChartBasedOnValues && this.hideChartBasedOnValues.length >  0 &&
        this.hideChartBasedOnValues.includes(this.value.toString())) {
      this.isMiniChartAvailable = false;
    }
    this.cd.markForCheck();
  }
}
